import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import parse from "html-react-parser";
import RichTextEditor from "./RichTextEditor";
import {
  RefreshCw,
  Loader2,
  ChevronUp,
  ChevronDown,
  Trash2,
  Type,
  Video,
  Image as ImageIcon,
  Paperclip,
  Pencil,
  Check,
  X,
  Plus,
} from "lucide-react";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : String(Date.now() + Math.random());

const newBlock = (type) => {
  if (type === "text") return { id: uid(), type, html: "" };
  if (type === "video") return { id: uid(), type, url: "" };
  if (type === "image") return { id: uid(), type, url: "", alt: "" };
  return { id: uid(), type: "file", url: "", name: "" };
};

const blockMeta = {
  text: { icon: <Type size={14} />, label: "Texte" },
  video: { icon: <Video size={14} />, label: "Vidéo" },
  image: { icon: <ImageIcon size={14} />, label: "Image" },
  file: { icon: <Paperclip size={14} />, label: "Fichier" },
};

const LessonEditor = ({ lesson, onSave, onDirtyChange, onRegenerateLesson, onRegenerateBlock }) => {
  const [title, setTitle] = useState(lesson.title);
  const [blocks, setBlocks] = useState(Array.isArray(lesson.blocks) ? lesson.blocks : []);
  // Un seul bloc texte en édition à la fois (CKEditor = instance unique, fiable)
  const [editingId, setEditingId] = useState(null);
  const [draftHtml, setDraftHtml] = useState("");
  // Modifications non enregistrées (remonté au parent via onDirtyChange)
  const [dirty, setDirty] = useState(false);
  const [regenLesson, setRegenLesson] = useState(false);
  const [regenBlockId, setRegenBlockId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setTitle(lesson.title);
    setBlocks(Array.isArray(lesson.blocks) ? lesson.blocks : []);
    setEditingId(null);
    setDraftHtml("");
    setDirty(false);
  }, [lesson.id]);

  // Informe le parent de l'état "dirty" (et le remet à zéro au démontage).
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
    return () => {
      if (onDirtyChange) onDirtyChange(false);
    };
  }, [dirty, onDirtyChange]);

  const handleRegenLesson = async () => {
    if (regenLesson) return;
    setRegenLesson(true);
    try {
      await onRegenerateLesson();
    } finally {
      if (mounted.current) setRegenLesson(false);
    }
  };

  const handleRegenBlock = async (id) => {
    if (regenBlockId) return;
    setRegenBlockId(id);
    try {
      await onRegenerateBlock(id);
    } finally {
      if (mounted.current) setRegenBlockId(null);
    }
  };

  // Upload S3 d'une image, via le même mécanisme que les images inline CKEditor
  // (POST /api/message/upload-inline → clé S3 ; rendu via le proxy /inline-image).
  const handleImageUpload = async (blockId, e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // permet de re-sélectionner le même fichier
    if (!file) return;
    setUploadError("");
    setUploadingId(blockId);
    try {
      const fd = new FormData();
      fd.append("media", file);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/message/upload-inline`,
        fd,
        { withCredentials: true }
      );
      const proxyUrl = `${process.env.REACT_APP_API_URL}/api/message/inline-image?key=${encodeURIComponent(
        res.data.key
      )}`;
      if (mounted.current) updateBlock(blockId, { url: proxyUrl });
    } catch (err) {
      if (mounted.current) setUploadError(err?.response?.data?.error || "Échec de l'upload de l'image");
    } finally {
      if (mounted.current) setUploadingId(null);
    }
  };

  const updateBlock = (id, patch) => {
    setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
    setDirty(true);
  };
  const removeBlock = (id) => {
    setBlocks((bs) => bs.filter((b) => b.id !== id));
    setDirty(true);
  };
  const addBlock = (type) => {
    setBlocks((bs) => [...bs, newBlock(type)]);
    setDirty(true);
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    const copy = [...blocks];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    setBlocks(copy);
    setDirty(true);
  };

  const handleSave = async () => {
    // onSave (parent) résout à false si la sauvegarde a échoué.
    const ok = await onSave({ title, blocks });
    if (ok !== false && mounted.current) setDirty(false);
  };

  const openEditor = (b) => {
    setEditingId(b.id);
    setDraftHtml(b.html || "");
  };
  const validateEditor = (id) => {
    updateBlock(id, { html: draftHtml });
    setEditingId(null);
    setDraftHtml("");
  };
  const cancelEditor = () => {
    setEditingId(null);
    setDraftHtml("");
  };

  return (
    <div className="lesson-editor">
      <div className="builder-section-head">
        <input
          className="lesson-title-input"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setDirty(true);
          }}
          placeholder="Titre de la leçon"
        />
        <div className="builder-row" style={{ gap: 8 }}>
          <button className="ai-btn" onClick={handleRegenLesson} disabled={regenLesson}>
            {regenLesson ? (
              <>
                <Loader2 size={15} className="builder-spin" /> Régénération…
              </>
            ) : (
              <>
                <RefreshCw size={15} /> Régénérer
              </>
            )}
          </button>
          <button className="greenButton" onClick={handleSave} disabled={regenLesson}>
            Enregistrer
          </button>
        </div>
      </div>

      {blocks.length === 0 && (
        <p className="builder-empty">Aucun bloc. Ajoutez du contenu ci-dessous.</p>
      )}

      {blocks.map((b, idx) => {
        const meta = blockMeta[b.type] || blockMeta.text;
        return (
          <div className="builder-block" key={b.id}>
            <div className="builder-block-head">
              <span className="builder-block-type">
                {meta.icon} {meta.label}
              </span>
              <div className="builder-row" style={{ gap: 4 }}>
                <button className="icon-btn" title="Monter" disabled={idx === 0} onClick={() => move(idx, -1)}>
                  <ChevronUp size={15} />
                </button>
                <button
                  className="icon-btn"
                  title="Descendre"
                  disabled={idx === blocks.length - 1}
                  onClick={() => move(idx, 1)}
                >
                  <ChevronDown size={15} />
                </button>
                {b.type === "text" && (
                  <button
                    className="ai-btn icon-only"
                    title="Régénérer ce bloc"
                    onClick={() => handleRegenBlock(b.id)}
                    disabled={regenBlockId === b.id}
                  >
                    {regenBlockId === b.id ? <Loader2 size={15} className="builder-spin" /> : <RefreshCw size={15} />}
                  </button>
                )}
                <button className="icon-btn danger" title="Supprimer" onClick={() => removeBlock(b.id)}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>

            {b.type === "text" &&
              (editingId === b.id ? (
                <div className="block-edit">
                  <RichTextEditor initialHtml={b.html || ""} onChange={(html) => setDraftHtml(html)} />
                  <div className="builder-row" style={{ gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                    <button className="mini-btn" onClick={cancelEditor}>
                      <X size={14} /> Annuler
                    </button>
                    <button className="greenButton" onClick={() => validateEditor(b.id)}>
                      <Check size={14} /> Valider
                    </button>
                  </div>
                </div>
              ) : (
                <div className="block-text">
                  <div className="block-preview">
                    {b.html ? parse(b.html) : <em className="builder-empty">Bloc vide</em>}
                  </div>
                  <button className="mini-btn" onClick={() => openEditor(b)}>
                    <Pencil size={14} /> Éditer
                  </button>
                </div>
              ))}

            {b.type === "video" && (
              <input
                className="builder-input"
                placeholder="URL YouTube / Shorts / Loom / Vimeo"
                value={b.url || ""}
                onChange={(e) => updateBlock(b.id, { url: e.target.value })}
              />
            )}
            {b.type === "image" &&
              (b.url ? (
                <div className="block-image-preview">
                  <img src={b.url} alt={b.alt || ""} />
                  <div className="builder-row" style={{ gap: 8, marginTop: 8 }}>
                    <label className="mini-btn">
                      <ImageIcon size={14} /> Remplacer
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        hidden
                        disabled={uploadingId === b.id}
                        onChange={(e) => handleImageUpload(b.id, e)}
                      />
                    </label>
                    <button className="mini-btn danger" onClick={() => updateBlock(b.id, { url: "" })}>
                      <X size={14} /> Retirer
                    </button>
                  </div>
                </div>
              ) : (
                <label className={`image-dropzone ${uploadingId === b.id ? "is-uploading" : ""}`}>
                  {uploadingId === b.id ? (
                    <>
                      <Loader2 size={18} className="builder-spin" /> Envoi en cours…
                    </>
                  ) : (
                    <>
                      <ImageIcon size={18} /> Cliquer pour téléverser une image
                      <span className="image-dropzone-hint">PNG, JPG ou WEBP</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    hidden
                    disabled={uploadingId === b.id}
                    onChange={(e) => handleImageUpload(b.id, e)}
                  />
                </label>
              ))}
            {b.type === "image" && uploadError && uploadingId !== b.id && (
              <p className="image-upload-error">{uploadError}</p>
            )}
            {b.type === "file" && (
              <>
                <input
                  className="builder-input"
                  placeholder="URL du fichier (PDF…)"
                  value={b.url || ""}
                  onChange={(e) => updateBlock(b.id, { url: e.target.value })}
                  style={{ marginBottom: 6 }}
                />
                <input
                  className="builder-input"
                  placeholder="Nom affiché"
                  value={b.name || ""}
                  onChange={(e) => updateBlock(b.id, { name: e.target.value })}
                />
              </>
            )}
          </div>
        );
      })}

      <div className="add-block-bar">
        <span className="add-block-label">
          <Plus size={14} /> Ajouter un bloc
        </span>
        <button className="mini-btn" onClick={() => addBlock("text")}>
          <Type size={14} /> Texte
        </button>
        <button className="mini-btn" onClick={() => addBlock("video")}>
          <Video size={14} /> Vidéo
        </button>
        <button className="mini-btn" onClick={() => addBlock("image")}>
          <ImageIcon size={14} /> Image
        </button>
        <button className="mini-btn" onClick={() => addBlock("file")}>
          <Paperclip size={14} /> Fichier
        </button>
      </div>
    </div>
  );
};

export default LessonEditor;
